// ─── Group Tulinh Livestream Modal ────────────────────────────────
// Modal livestream cho group — game Tứ Linh.
// Parse raw logs trực tiếp (giống TulinhLivestreamModal account) thay vì
// đọc shared session cache — tránh mất lastResult khi server cache clear.
// Giữ header member dots (trạng thái hoạt động các thành viên).
// Không có console logs / controls (group chỉ xem session).
// Logic giống Xóc Đĩa: 4 đĩa, mỗi đĩa ĐỎ hoặc TRẮNG.
//
// cmds: 1950 (plugin list), 202 (session data), 904 (new round), 907 (rolling), 908 (ended), 207 (jackpot), 10000 (jackpot list)

import React from 'react';
import {
  type LivestreamLogEntry,
  type SessionState,
  type HistoryEntry,
  type LivestreamAccount,
  initialSession,
  extractExtensionPayload,
  useLivestreamStream,
  useCountdown,
  formatCoin,
  formatJackpot,
  statusColorClass,
  statusPingColorClass,
  countdownColorClass,
  sessionStatusColorClass,
} from './livestream-base';
import {
  type GroupLivestreamModalProps,
  parseTypegame,
  gameName,
  GroupMemberDots,
} from './group-livestream-base';
import { GroupSessionHistoryTable } from './GroupSessionHistoryTable';

// ─── Game-specific types ───────────────────────────────────────────

/**
 * Tứ Linh chơi giống Xóc Đĩa: 4 đĩa, mỗi đĩa ĐỎ hoặc TRẮNG.
 * rt = số mặt TRẮNG (whiteCount 0-4) từ server.
 *   0 = 4 đỏ 0 trắng → RED_X4 + EVEN
 *   1 = 3 đỏ 1 trắng → RED_X3 + ODD
 *   2 = 2 đỏ 2 trắng → EVEN
 *   3 = 1 đỏ 3 trắng → WHITE_X3 + ODD
 *   4 = 0 đỏ 4 trắng → WHITE_X4 + EVEN
 */
interface TulinhResult {
  whiteCount: number;
  redCount: number;
  chanLe: 'CHẴN' | 'LẺ';
  betTypes: string[];
  disks: boolean[];
}

function parseTulinhResult(whiteCount: number): TulinhResult {
  const redCount = 4 - whiteCount;
  const chanLe = whiteCount % 2 === 0 ? 'CHẴN' : 'LẺ';
  const betTypes: string[] = [];
  betTypes.push(chanLe === 'CHẴN' ? 'EVEN' : 'ODD');
  if (whiteCount === 0) betTypes.push('RED_X4');
  else if (whiteCount === 1) betTypes.push('RED_X3');
  else if (whiteCount === 3) betTypes.push('WHITE_X3');
  else if (whiteCount === 4) betTypes.push('WHITE_X4');
  const disks = Array.from({ length: 4 }, (_, i) => i < whiteCount);
  return { whiteCount, redCount, chanLe, betTypes, disks };
}

// ─── Component ─────────────────────────────────────────────────────

export const GroupTulinhLivestreamModal: React.FC<GroupLivestreamModalProps> = ({
  isOpen,
  onClose,
  groupName,
  groupId,
  members,
  gameType,
}) => {
  // Tìm account đang RUNNING để subscribe SSE raw logs
  const runningMember = members.find((m) => m.status === 'RUNNING') ?? members[0];
  const anyRunning = members.some((m) => m.status === 'RUNNING');

  // Convert IAccountDataResponse → LivestreamAccount cho useLivestreamStream
  const account: LivestreamAccount | null = runningMember
    ? {
        _id: runningMember._id,
        username: runningMember.username,
        displayname: runningMember.displayname ?? '',
        config: runningMember.config,
      }
    : null;

  const [session, setSession] = React.useState<SessionState<TulinhResult>>(() => ({
    ...initialSession<TulinhResult>(),
    timeBet: 23,
  }));

  const parseSessionData = React.useCallback((entry: LivestreamLogEntry) => {
    const payload = extractExtensionPayload(entry);
    if (!payload) return;
    const cmd = payload.cmd;

    // cmd 1950: Plugin list — bỏ qua
    if (cmd === 1950) return;

    // cmd 202: Session data — rt (whiteCount) + coins + jackpot
    if (cmd === 202) {
      const sid = payload.sid as number;
      const rt = payload.rt as number | undefined;
      const coins = (payload.coins as number[]) ?? [];
      const jackpotAmount = payload.J as number | undefined;
      const gS = payload.gS as number | undefined;

      const whiteCount = rt !== undefined ? rt : null;
      const result = whiteCount !== null ? parseTulinhResult(whiteCount) : null;

      setSession((prev) => ({
        ...prev,
        sessionId: sid,
        status: gS === 4 ? 'BETTING' : gS === 3 ? 'ROLLING' : prev.status,
        coins: coins.length > 0 ? coins : prev.coins,
        jackpot: jackpotAmount ?? prev.jackpot,
        lastResult: result ?? prev.lastResult,
      }));
      return;
    }

    // cmd 904: New round — BETTING
    if (cmd === 904) {
      const sid = payload.sid as number;
      setSession((prev) => ({
        ...prev,
        sessionId: sid,
        status: 'BETTING',
        countdown: prev.timeBet,
      }));
      return;
    }

    // cmd 907: Rolling — kết quả xóc đĩa mới (rt = whiteCount)
    if (cmd === 907) {
      const rt = payload.rt as number | undefined;
      const whiteCount = rt !== undefined ? rt : null;
      if (whiteCount !== null) {
        const result = parseTulinhResult(whiteCount);
        const sid = (payload.sid as number) ?? session.sessionId ?? 0;
        const historyEntry: HistoryEntry<TulinhResult> = {
          sessionId: sid,
          result,
          resultRaw: String(whiteCount),
        };
        setSession((prev) => {
          const exists = prev.history.some((h) => h.sessionId === sid);
          const history = exists ? prev.history : [historyEntry, ...prev.history].slice(0, 50);
          return {
            ...prev,
            status: 'ROLLING',
            countdown: 0,
            lastResult: result,
            history,
          };
        });
      }
      return;
    }

    // cmd 908: Ended — kết quả xóc đĩa cuối (rt = whiteCount)
    if (cmd === 908) {
      const rt = payload.rt as number | undefined;
      const whiteCount = rt !== undefined ? rt : null;
      if (whiteCount !== null) {
        const result = parseTulinhResult(whiteCount);
        const sid = (payload.sid as number) ?? session.sessionId ?? 0;
        const historyEntry: HistoryEntry<TulinhResult> = {
          sessionId: sid,
          result,
          resultRaw: String(whiteCount),
        };
        setSession((prev) => {
          const exists = prev.history.some((h) => h.sessionId === sid);
          const history = exists ? prev.history : [historyEntry, ...prev.history].slice(0, 50);
          return {
            ...prev,
            status: 'ENDED',
            countdown: 0,
            lastResult: result,
            history,
          };
        });
      }
      return;
    }

    // cmd 207: Jackpot
    if (cmd === 207) {
      const ba = payload.ba as number;
      setSession((prev) => ({ ...prev, jackpot: ba }));
      return;
    }

    // cmd 10000: Jackpot list
    if (cmd === 10000) {
      const js = payload.Js as Array<{ gid: number; ba: number }>;
      if (js) {
        const tlJackpot = js.find((j) => j.gid === 14);
        if (tlJackpot) setSession((prev) => ({ ...prev, jackpot: tlJackpot.ba }));
      }
      return;
    }
  }, []);

  useCountdown(setSession);
  // Nhóm đã Stop vẫn mở được modal (để xem lại lịch sử phiên) nhưng account không
  // còn WS nào — mở SSE lúc đó chỉ để nó lỗi rồi retry backoff mãi tới khi đóng.
  const stream = useLivestreamStream<TulinhResult>(account, isOpen && anyRunning, parseSessionData);

  if (!isOpen) return null;

  // Ưu tiên `gameType` của NHÓM (router modal đã resolve): `config` của account
  // có thể rỗng/không parse được — nhóm đã Stop là ca thường gặp — và khi đó bảng
  // "show phiên" nhận typegame '?' rồi in ra eid thô (5, 1, 3) thay vì nhãn cửa.
  const typegame =
    gameType && gameType !== '?' ? gameType : parseTypegame(runningMember?.config);
  const gName = gameName(typegame);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm">
      <div className="relative flex max-h-[88vh] w-full max-w-5xl flex-col rounded-2xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        {/* Close */}
        <button
          onClick={() => {
            stream.stopStream();
            onClose();
          }}
          className="absolute right-4 top-4 rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-200"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header + member dots */}
        <div className="mb-4 pr-10">
          <h3 className="flex items-center gap-2 text-lg font-extrabold tracking-tight text-white">
            <span className="relative flex h-3 w-3">
              <span
                className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${statusPingColorClass(stream.status)}`}
              />
              <span
                className={`relative inline-flex h-3 w-3 rounded-full ${statusColorClass(stream.status)}`}
              />
            </span>
            Group Livestream — {groupName}
          </h3>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold text-slate-400">
            <span>
              Trạng thái:{' '}
              <span className="font-bold uppercase text-slate-300">{stream.status}</span>
            </span>
            <span className="text-slate-600">|</span>
            <span>
              Game:{' '}
              <span className="font-bold text-purple-400">
                {gName} ({typegame})
              </span>
            </span>
            <span className="text-slate-600">|</span>
            <GroupMemberDots members={members} />
          </div>
        </div>

        {/* Session stats grid */}
        <div className="mb-4 grid grid-cols-4 gap-3">
          <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Phiên
            </div>
            <div className="mt-1 text-lg font-extrabold text-blue-400">
              {session.sessionId ? `#${session.sessionId}` : '---'}
            </div>
          </div>
          <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Đặt phiên
            </div>
            <div className={`mt-1 text-lg font-extrabold ${countdownColorClass(session)}`}>
              {session.countdown > 0
                ? `${session.countdown}s`
                : session.sessionId
                  ? 'Chờ...'
                  : '---'}
            </div>
          </div>
          <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Trạng thái
            </div>
            <div
              className={`mt-1 text-sm font-extrabold ${sessionStatusColorClass(session.status)}`}
            >
              {session.status || '---'}
            </div>
          </div>
          <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Hũ</div>
            <div className="mt-1 text-lg font-extrabold text-amber-400">
              {session.jackpot > 0 ? formatJackpot(session.jackpot) : '---'}
            </div>
          </div>
        </div>

        {/* Coins */}
        {session.coins.length > 0 && (
          <div className="mb-4 mt-4 rounded-lg border border-slate-700 bg-slate-800/50 p-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Mệnh giá cược
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {session.coins.map((coin, i) => (
                <span
                  key={i}
                  className="rounded-lg border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs font-bold text-slate-300"
                >
                  {formatCoin(coin)}
                </span>
              ))}
            </div>
          </div>
        )}
        {/* Session history table (show phiên) — thay cho "kết quả gần nhất" */}
        <GroupSessionHistoryTable
          groupId={groupId}
          typegame={typegame}
          members={members}
          isOpen={isOpen}
        />

        {/* Idle notice */}
        {!anyRunning && (
          <div className="mb-4 rounded-lg border border-dashed border-slate-700 bg-slate-800/30 p-4 text-center text-xs font-semibold text-slate-400">
            Chưa có tài khoản nào trong nhóm đang chạy livestream.
          </div>
        )}

        {/* Footer */}
        <div className="mt-auto flex justify-end gap-2 border-t border-slate-800 pt-4">
          <button
            onClick={() => {
              stream.stopStream();
              onClose();
            }}
            className="rounded-lg bg-slate-800 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-slate-700"
          >
            Đóng
          </button>
        </div>
      </div>
    </div>
  );
};
